import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { LayoutDashboard, BookOpen, CalendarDays, LogOut, Menu, DollarSign, CalendarOff, Clock, PanelLeftClose, PanelLeft, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Sheet, SheetContent } from '../components/ui/sheet';
import { getInitials } from '../lib/utils';
import { useState } from 'react';

const navItems = [
  { to: '/instructor/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/instructor/batches', icon: BookOpen, label: 'My Batches' },
  { to: '/instructor/schedule', icon: CalendarDays, label: 'My Schedule' },
  { to: '/instructor/availability', icon: Clock, label: 'Availability' },
  { to: '/instructor/leaves', icon: CalendarOff, label: 'My Leaves' },
  { to: '/instructor/payouts', icon: DollarSign, label: 'Payouts' },
  { to: '/instructor/profile', icon: UserCircle, label: 'My Profile' },
];

function SidebarContent({ onClose, collapsed }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <div className="flex items-center gap-2">
          <div className="h-10 w-10 shrink-0 rounded-xl overflow-hidden bg-white flex items-center justify-center">
            <img src="/logo.png" alt="Soul Sanskar" className="h-14 w-14 object-contain scale-150" />
          </div>
          {!collapsed && (
            <div>
              <h1 className="font-heading text-base font-semibold tracking-tight leading-tight">Soul Sanskar</h1>
              <p className="text-[10px] text-muted-foreground">Instructor Panel</p>
            </div>
          )}
        </div>
      </div>
      <nav className="flex-1 p-2 space-y-0.5">
        {navItems.map(item => (
          <NavLink key={item.to} to={item.to} onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-colors duration-200 ${isActive ? 'bg-primary/10 text-primary border-l-2 border-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`
            }
            title={collapsed ? item.label : undefined}>
            <item.icon className="h-4 w-4 shrink-0" />{!collapsed && item.label}
          </NavLink>
        ))}
      </nav>
      <div className={`p-3 border-t ${collapsed ? 'px-2' : ''}`}>
        {!collapsed && (
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-8 w-8"><AvatarFallback className="bg-primary/10 text-primary text-xs">{getInitials(user?.name)}</AvatarFallback></Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground">Instructor</p>
            </div>
          </div>
        )}
        <Button variant="ghost" size="sm" className={`w-full ${collapsed ? 'px-2' : 'justify-start'} text-muted-foreground`} onClick={() => { logout(); navigate('/login'); }}>
          <LogOut className="h-4 w-4 mr-2" />{!collapsed && 'Sign out'}
        </Button>
      </div>
    </div>
  );
}

export default function InstructorLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  return (
    <div className="flex h-screen bg-background">
      <aside className={`hidden lg:flex ${collapsed ? 'w-[68px]' : 'w-[248px]'} shrink-0 border-r bg-white/70 backdrop-blur flex-col transition-all duration-300 relative`}>
        <SidebarContent onClose={() => {}} collapsed={collapsed} />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 z-10 h-6 w-6 rounded-full border bg-white shadow-sm flex items-center justify-center hover:bg-muted transition-colors" data-testid="sidebar-collapse-button">
          {collapsed ? <PanelLeft className="h-3 w-3" /> : <PanelLeftClose className="h-3 w-3" />}
        </button>
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[248px]"><SidebarContent onClose={() => setMobileOpen(false)} collapsed={false} /></SheetContent>
      </Sheet>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b bg-white/60 backdrop-blur flex items-center px-4 lg:px-6 gap-3">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}><Menu className="h-5 w-5" /></Button>
          <div className="flex-1" />
        </header>
        <main className="flex-1 overflow-auto p-4 sm:p-6 lg:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
